import {
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostPostsViraisMutation } from '@/services/queries/social-media';
import { ModalFormData, modalFormSchema } from './schemas';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PostsViraisCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  onDone?: () => void;
}

const MAX_POSTS = 5;

export const PostsViraisCreateModal = ({ isOpen, onClose, onDone }: PostsViraisCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { control, register, handleSubmit, reset } = useForm<ModalFormData>({
    resolver: zodResolver(modalFormSchema),
    defaultValues: {
      team_uuid: '',
      urls: [{ value: '' }],
      enabled: true,
    },
  });

  const {
    fields: urlFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'urls',
  });

  const postMutation = usePostPostsViraisMutation();

  const handleConfirm = useCallback(() => {
    handleSubmit(data => {
      const payload = {
        team_uuid: data.team_uuid,
        enabled: data.enabled,
        urls: data.urls.map(u => u.value),
      };

      postMutation.mutate(payload, {
        onSuccess: () => {
          toast({
            title: t('pages.application_social_media.social_media_post_viral.modal_create.save_success'),
            status: 'success',
          });
          reset();
          onDone?.();
          onClose();
        },
        onError: () => {
          toast({
            title: t('pages.application_social_media.social_media_post_viral.modal_create.save_failed'),
            status: 'error',
          });
        },
      });
    })();
  }, [handleSubmit, postMutation, toast, onDone, onClose, reset, t]);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justify='space-between' align='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_social_media.social_media_post_viral.modal_create.create_title')}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <Flex direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_social_media.social_media_post_viral.modal_create.team')}
              placeholder={t('pages.application_social_media.social_media_post_viral.modal_create.team')}
              showFlag
              width='100%'
              usePortal={false}
            />

            <Flex direction='column' gap={4}>
              {urlFields.map((field, index) => (
                <Flex key={field.id} direction={{ base: 'column', md: 'row' }} gap={3} align='center'>
                  <Input
                    placeholder={t('pages.application_social_media.social_media_post_viral.modal_create.placeholder_url')}
                    {...register(`urls.${index}.value`)}
                  />
                  <IconButton
                    icon={<PiTrash />}
                    aria-label='Remove'
                    size='sm'
                    colorScheme='red'
                    variant='ghost'
                    onClick={() => remove(index)}
                  />
                </Flex>
              ))}

              {urlFields.length < MAX_POSTS && (
                <Button
                  onClick={() => append({ value: '' })}
                  leftIcon={<PiPlus />}
                  variant='link'
                  color='brand.500'
                  fontWeight='normal'
                  alignSelf='flex-start'
                >
                  {t('pages.application_social_media.social_media_post_viral.modal_create.add_button')}
                </Button>
              )}

              {urlFields.length >= MAX_POSTS && (
                <Text fontSize='xs' color='gray.500'>
                  {t('pages.application_social_media.social_media_post_viral.modal_create.limit_reached', {
                    count: MAX_POSTS,
                  })}
                </Text>
              )}
            </Flex>

            <Flex align='center' gap={3}>
              <Text fontWeight='medium'>
                {t('pages.application_social_media.social_media_post_viral.modal_create.enable_area')}
              </Text>
              <Controller
                control={control}
                name='enabled'
                render={({ field }) => <Switch size='sm' isChecked={field.value} onChange={field.onChange} />}
              />
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='flex-end' gap={4}>
          <Button variant='outlined' color='gray.600' onClick={onClose}>
            {t('pages.application_social_media.social_media_post_viral.modal_create.create_cancel')}
          </Button>
          <Button onClick={handleConfirm} bg='green.500' px={10}>
            {t('pages.application_social_media.social_media_post_viral.modal_create.create_save')}
          </Button>
        </ModalFooter>

        <CenteredSpinner isLoading={postMutation.isLoading} />
      </ModalContent>
    </Modal>
  );
};
